import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { BarChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Palette } from '@/themes/themeOverrides/Palette';

const StyledTooltip = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    backgroundColor: Palette.white,
    padding: '12px 16px',
    borderRadius: '16px',
    boxShadow: Palette.boxShadow.elevation16,
}));

const formatRangeDate = date => {
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return '';

    const day = parsedDate.getDate().toString().padStart(2, '0');
    const month = (parsedDate.getMonth() + 1).toString().padStart(2, '0');

    return `${day}.${month}`;
};

const formatPeriodLabel = data => {
    const start = data?.periodStart?.date;
    const end = data?.periodEnd?.date;
    if (!start || !end) return '';

    const startDate = new Date(start);
    const endDate = new Date(end);
    const isSameDay =
        !Number.isNaN(startDate.getTime()) &&
        !Number.isNaN(endDate.getTime()) &&
        startDate.getFullYear() === endDate.getFullYear() &&
        startDate.getMonth() === endDate.getMonth() &&
        startDate.getDate() === endDate.getDate();

    if (isSameDay) return formatRangeDate(startDate);

    const startLabel = formatRangeDate(start);
    const endLabel = formatRangeDate(end);
    if (!startLabel || !endLabel) return startLabel || endLabel;

    return `${startLabel} - ${endLabel}`;
};

const formatTooltipValue = value => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return '0';

    return String(numericValue);
};

const StatsBarTooltip = ({ active, payload, dataKey }) => {
    if (!active || !payload?.length) return null;

    const data = payload[0].payload;
    const periodLabel = formatPeriodLabel(data);
    const value = formatTooltipValue(data?.[dataKey]);

    return (
        <StyledTooltip>
            {periodLabel ? (
                <Typography variant='caption' sx={{ fontSize: '11px', lineHeight: '16px' }}>
                    {periodLabel}
                </Typography>
            ) : null}
            <Typography variant='body2' sx={{ fontWeight: 700, fontSize: '14px', lineHeight: '20px' }}>
                {value}
            </Typography>
        </StyledTooltip>
    );
};

StatsBarTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(
        PropTypes.shape({
            payload: PropTypes.shape({
                periodStart: PropTypes.shape({
                    date: PropTypes.string,
                }),
                periodEnd: PropTypes.shape({
                    date: PropTypes.string,
                }),
            }),
        }),
    ),
    dataKey: PropTypes.string.isRequired,
};

export const StatsSparklineChart = ({ data, dataKey, children }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                width: '100%',
                maxWidth: '250px',
                height: 56,
                position: 'relative',
                zIndex: isHovered ? 2 : 1,
                overflow: 'visible',
                '& .recharts-wrapper': { minWidth: '200px', overflow: 'visible' },
                '& .recharts-surface': { overflow: 'visible' },
            }}
        >
            <ResponsiveContainer>
                <BarChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} style={{ margin: '0 0 0 -2px', padding: '0' }} barCategoryGap={1}>
                    <Tooltip
                        content={<StatsBarTooltip dataKey={dataKey} />}
                        cursor={false}
                        allowEscapeViewBox={{ x: true, y: true }}
                        wrapperStyle={{ zIndex: 2, pointerEvents: 'none', outline: 'none' }}
                    />
                    {children}
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
};

StatsSparklineChart.propTypes = {
    data: PropTypes.array,
    dataKey: PropTypes.string.isRequired,
    children: PropTypes.node,
};
